// src/AppShellSidebar/NavRow.tsx
import React from "react";
import { css, cx } from "@emotion/css";
import { Tooltip, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import "@hopdrive/sdk-theme";
import { jsx, jsxs } from "react/jsx-runtime";
var NavRow = React.forwardRef(function NavRow2({ condensed, icon: Icon, iconSize, label, active, disabled, iconBadge, endAdornment, className }, ref) {
  const theme = useTheme();
  const cls = useStyles(theme, { condensed });
  const size = iconSize ?? 20;
  const row = /* @__PURE__ */ jsxs(
    "div",
    {
      ref,
      className: cx(cls.row, active && cls.rowActive, disabled && cls.rowDisabled, className),
      "aria-disabled": disabled || void 0,
      children: [
        Icon ? /* @__PURE__ */ jsxs("span", { className: cls.iconBox, children: [
          /* @__PURE__ */ jsx(Icon, { size }),
          iconBadge
        ] }) : null,
        condensed ? null : /* @__PURE__ */ jsx(
          Typography,
          {
            sx: { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", ml: "12px", fontSize: 14, fontWeight: 400 },
            children: label
          }
        ),
        condensed ? null : endAdornment
      ]
    }
  );
  if (condensed && label) {
    return /* @__PURE__ */ jsx(Tooltip, { title: label, placement: "right", children: row });
  }
  return row;
});
var useStyles = (theme, props) => ({
  row: css`
    position: relative;
    overflow: hidden;
    white-space: nowrap;
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: ${props.condensed ? `center` : `flex-start`};
    width: 100%;
    padding: ${props.condensed ? `6px` : `6px 8px`};
    border-radius: 6px;
    margin-bottom: 4px;
    background-color: transparent;
    color: ${theme.palette.text.contrastPrimary};
    cursor: pointer;
    user-select: none;
    transition: background-color 0.1s ease-in-out;
    :hover,
    :focus-visible {
      background-color: ${theme.palette.action.contrastHover};
    }
    :active {
      background-color: ${theme.palette.action.contrastSelected};
    }
  `,
  rowActive: css`
    background-color: ${alpha(theme.palette.primary.light, 0.09)} !important;
    color: ${theme.palette.primary.main} !important;
  `,
  rowDisabled: css`
    opacity: 0.5;
    cursor: not-allowed;
    :hover,
    :active {
      background-color: transparent;
    }
  `,
  iconBox: css`
    position: relative;
    display: flex;
    align-items: center;
    flex-shrink: 0;
  `
});
var NavRow_default = NavRow;

// src/AppShellSidebar/NavBadge.tsx
import { css as css2, cx as cx2 } from "@emotion/css";
import { useTheme as useTheme2 } from "@mui/material";
import { jsx as jsx2 } from "react/jsx-runtime";
var NavBadge = ({ count, maxCount = 99, corner, className }) => {
  const theme = useTheme2();
  const cls = useStyles2(theme);
  if (!count || count <= 0) return null;
  return /* @__PURE__ */ jsx2("span", { className: cx2(cls.badge, corner ? cls.badgeCorner : cls.badgeInline, className), children: count > maxCount ? `${maxCount}+` : count });
};
var useStyles2 = (theme) => ({
  badge: css2`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background-color: ${theme.palette.error.main};
    color: ${theme.palette.error.contrastText};
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
  `,
  badgeInline: css2`
    margin-left: auto;
  `,
  badgeCorner: css2`
    position: absolute;
    top: -6px;
    right: -8px;
  `
});
var NavBadge_default = NavBadge;

// src/AppShellSidebar/interactiveReset.ts
import { css as css3 } from "@emotion/css";
var interactiveReset = css3`
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  margin: 0;
  background: none;
  font: inherit;
  text-align: inherit;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
`;

// src/AppShellSidebar/AppShellSidebarItem.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
var AppShellSidebarItem = ({
  condensed,
  label,
  icon,
  route,
  active,
  disabled,
  badgeCount,
  onNavigate,
  onAction
}) => {
  const row = /* @__PURE__ */ jsx3(
    NavRow_default,
    {
      condensed,
      icon,
      label,
      active: route ? active : false,
      disabled,
      iconBadge: condensed ? /* @__PURE__ */ jsx3(NavBadge_default, { count: badgeCount, corner: true }) : void 0,
      endAdornment: /* @__PURE__ */ jsx3(NavBadge_default, { count: badgeCount })
    }
  );
  if (disabled) return row;
  if (route) {
    const handleClick = (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      event.preventDefault();
      onNavigate?.(route);
    };
    return /* @__PURE__ */ jsx3("a", { href: route, className: interactiveReset, onClick: handleClick, "aria-current": active ? "page" : void 0, children: row });
  }
  return /* @__PURE__ */ jsx3("button", { type: "button", className: interactiveReset, onClick: onAction, children: row });
};
var AppShellSidebarItem_default = AppShellSidebarItem;

// src/AppShellSidebar/AppShellSidebarSection.tsx
import { css as css4 } from "@emotion/css";
import { Typography as Typography2, useTheme as useTheme3 } from "@mui/material";
import "@hopdrive/sdk-theme";
import { jsx as jsx4, jsxs as jsxs2 } from "react/jsx-runtime";
var AppShellSidebarSection = ({ condensed, title, withDivider, children }) => {
  const theme = useTheme3();
  const cls = useStyles3(theme);
  return /* @__PURE__ */ jsxs2("section", { className: cls.section, children: [
    withDivider ? /* @__PURE__ */ jsx4("div", { className: cls.divider }) : null,
    /* @__PURE__ */ jsxs2("div", { className: cls.body, children: [
      title && !condensed ? /* @__PURE__ */ jsx4(
        Typography2,
        {
          sx: {
            p: "6px",
            color: "text.contrastDisabled",
            marginBottom: "4px",
            lineHeight: 1,
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          },
          children: title
        }
      ) : null,
      children
    ] })
  ] });
};
var useStyles3 = (theme) => ({
  section: css4`
    width: 100%;
  `,
  // The section owns its own horizontal inset, rather than depending on a global
  // sidebar padding; nav rows and the section title align to this 6px.
  body: css4`
    padding: 0 12px;
  `,
  divider: css4`
    height: 1px;
    margin: 12px 0;
    background-color: ${theme.palette.dividerContrast};
  `
});
var AppShellSidebarSection_default = AppShellSidebarSection;

// src/AppShellSidebar/AppShellSidebarItemGroup.tsx
import React2 from "react";
import { css as css5 } from "@emotion/css";
import { Box, Collapse, Menu, Typography as Typography3, useTheme as useTheme4 } from "@mui/material";
import { BiChevronDown, BiChevronUp } from "react-icons/bi";
import "@hopdrive/sdk-theme";
import { Fragment, jsx as jsx5, jsxs as jsxs3 } from "react/jsx-runtime";
var AppShellSidebarItemGroup = ({ condensed, label, icon, items, hasActiveChild = false }) => {
  const theme = useTheme4();
  const cls = useStyles4(theme);
  const rowRef = React2.useRef(null);
  const [menuOpen, setMenuOpen] = React2.useState(false);
  const [open, setOpen] = React2.useState(hasActiveChild);
  const handleRowClick = () => {
    if (condensed) setMenuOpen(true);
    else setOpen((prev) => !prev);
  };
  const closeOnUse = (item) => ({
    ...item,
    condensed: false,
    onNavigate: item.onNavigate ? (route) => {
      setMenuOpen(false);
      item.onNavigate?.(route);
    } : void 0,
    onAction: item.onAction ? () => {
      setMenuOpen(false);
      item.onAction?.();
    } : void 0
  });
  const row = /* @__PURE__ */ jsx5(
    "button",
    {
      type: "button",
      ref: rowRef,
      className: interactiveReset,
      onClick: handleRowClick,
      "aria-expanded": condensed ? menuOpen : open,
      children: /* @__PURE__ */ jsx5(
        NavRow_default,
        {
          condensed,
          icon,
          label,
          endAdornment: open ? /* @__PURE__ */ jsx5(BiChevronUp, { size: 18, className: cls.chevron }) : /* @__PURE__ */ jsx5(BiChevronDown, { size: 18, className: cls.chevron })
        }
      )
    }
  );
  if (condensed) {
    return /* @__PURE__ */ jsxs3(Fragment, { children: [
      row,
      /* @__PURE__ */ jsxs3(
        Menu,
        {
          anchorEl: rowRef.current,
          open: menuOpen,
          onClose: () => setMenuOpen(false),
          anchorOrigin: { vertical: "top", horizontal: "right" },
          transformOrigin: { vertical: "top", horizontal: "left" },
          slotProps: {
            paper: {
              sx: { ml: 1, minWidth: 200, bgcolor: "nav.sidebar", color: "text.contrastPrimary", px: "6px" }
            }
          },
          children: [
            /* @__PURE__ */ jsx5(
              Typography3,
              {
                sx: {
                  p: "4px 12px 8px 12px",
                  color: "text.contrastSecondary",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase"
                },
                children: label
              }
            ),
            items.map((item, index) => /* @__PURE__ */ jsx5(AppShellSidebarItem_default, { ...closeOnUse(item) }, item.route ?? `${item.label}-${index}`))
          ]
        }
      )
    ] });
  }
  return /* @__PURE__ */ jsxs3(Fragment, { children: [
    row,
    /* @__PURE__ */ jsx5(Collapse, { in: open, unmountOnExit: true, children: /* @__PURE__ */ jsx5(Box, { sx: { pl: 2 }, children: items.map((item, index) => /* @__PURE__ */ jsx5(AppShellSidebarItem_default, { ...item, condensed: false }, item.route ?? `${item.label}-${index}`)) }) })
  ] });
};
var useStyles4 = (theme) => ({
  chevron: css5`
    flex-shrink: 0;
    margin-left: 8px;
    color: ${theme.palette.text.contrastSecondary};
  `
});
var AppShellSidebarItemGroup_default = AppShellSidebarItemGroup;

// src/AppShellSidebar/AppShellSidebar.tsx
import React3 from "react";
import { css as css6, cx as cx3 } from "@emotion/css";
import { CircularProgress, IconButton, InputBase, Popover, Tooltip as Tooltip2, useTheme as useTheme5 } from "@mui/material";
import { BiSearch } from "react-icons/bi";
import "@hopdrive/sdk-theme";

// src/AppShellSidebar/constants.ts
var SIDEBAR_WIDTH_DEFAULT = 240;
var SIDEBAR_WIDTH_MIN = 200;
var SIDEBAR_WIDTH_MAX = 280;
var SIDEBAR_RAIL_WIDTH = 64;

// src/utils/clampSidebarWidth.ts
function clampSidebarWidth(width) {
  if (typeof width !== "number" || !Number.isFinite(width)) return SIDEBAR_WIDTH_DEFAULT;
  return Math.min(SIDEBAR_WIDTH_MAX, Math.max(SIDEBAR_WIDTH_MIN, Math.round(width)));
}

// src/utils/filterNavSections.ts
var matches = (label, query) => label.toLowerCase().includes(query.toLowerCase());
function filterNavSections(sections, query) {
  const q = query.trim();
  if (!q) return sections;
  const filterEntry = (entry) => {
    if (entry.type === "group") {
      if (matches(entry.label, q)) return entry;
      const children = entry.children.filter((child) => matches(child.label, q));
      return children.length ? { ...entry, children } : null;
    }
    return matches(entry.label, q) ? entry : null;
  };
  return sections.map((section) => ({ ...section, entries: section.entries.map(filterEntry).filter((entry) => entry !== null) })).filter((section) => section.entries.length > 0);
}

// src/utils/matchRoute.ts
var normalizePath = (path) => path.replace(/\/+$/, "") || "/";
function matchRoute(route, currentPath) {
  if (!route || !currentPath) return false;
  const target = normalizePath(route);
  const current = normalizePath(currentPath);
  if (target === current) return true;
  if (target === "/") return false;
  return current.startsWith(target) && current.charAt(target.length) === "/";
}

// src/utils/resolveActiveEntry.ts
function resolveActiveEntry(entries, currentPath) {
  let winner = null;
  let winnerLength = -1;
  const consider = (candidate) => {
    if (candidate.route && matchRoute(candidate.route, currentPath)) {
      const length = normalizePath(candidate.route).length;
      if (length > winnerLength) {
        winner = candidate.route;
        winnerLength = length;
      }
    }
    candidate.children?.forEach(consider);
  };
  entries.forEach(consider);
  return winner;
}

// src/AppShellSidebar/AppShellSidebar.tsx
import { Fragment as Fragment2, jsx as jsx6, jsxs as jsxs4 } from "react/jsx-runtime";
var AppShellSidebar = ({
  sections,
  currentPath,
  navigate,
  condensed,
  pending,
  filterable = true,
  logo,
  condensedLogo,
  onBrandClick,
  footer,
  width,
  resizable,
  onWidthChange
}) => {
  const theme = useTheme5();
  const expandedWidth = clampSidebarWidth(width);
  const cls = useStyles5(theme, { width: condensed ? SIDEBAR_RAIL_WIDTH : expandedWidth });
  const [query, setQuery] = React3.useState("");
  const [filterAnchor, setFilterAnchor] = React3.useState(null);
  const visibleSections = filterNavSections(sections, filterable ? query : "");
  const candidates = sections.flatMap(
    (section) => section.entries.map(
      (entry) => entry.type === "group" ? { children: entry.children.map((child) => ({ route: child.route })) } : { route: entry.type === "item" ? entry.route : void 0 }
    )
  );
  const activeRoute = resolveActiveEntry(candidates, currentPath);
  const renderEntry = (entry, index) => {
    if (entry.type === "group") {
      return /* @__PURE__ */ jsx6(
        AppShellSidebarItemGroup_default,
        {
          condensed,
          label: entry.label,
          icon: entry.icon,
          hasActiveChild: entry.children.some((child) => child.route === activeRoute),
          items: entry.children.map((child) => ({
            label: child.label,
            icon: child.icon,
            route: child.route,
            badgeCount: child.badgeCount,
            disabled: child.disabled,
            active: child.route === activeRoute,
            onNavigate: navigate
          }))
        },
        `group-${entry.label}`
      );
    }
    if (entry.type === "action") {
      return /* @__PURE__ */ jsx6(
        AppShellSidebarItem_default,
        {
          condensed,
          label: entry.label,
          icon: entry.icon,
          disabled: entry.disabled,
          onAction: entry.onAction
        },
        `action-${entry.label}-${index}`
      );
    }
    return /* @__PURE__ */ jsx6(
      AppShellSidebarItem_default,
      {
        condensed,
        label: entry.label,
        icon: entry.icon,
        route: entry.route,
        badgeCount: entry.badgeCount,
        disabled: entry.disabled,
        active: entry.route === activeRoute,
        onNavigate: navigate
      },
      entry.route
    );
  };
  const handleResizeStart = (event) => {
    const handle = event.currentTarget;
    const startX = event.clientX;
    const startWidth = expandedWidth;
    handle.setPointerCapture(event.pointerId);
    const onMove = (move) => onWidthChange?.(clampSidebarWidth(startWidth + (move.clientX - startX)));
    const onEnd = () => {
      handle.removeEventListener("pointermove", onMove);
      handle.removeEventListener("pointerup", onEnd);
      handle.removeEventListener("pointercancel", onEnd);
    };
    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", onEnd);
    handle.addEventListener("pointercancel", onEnd);
  };
  const filterField = /* @__PURE__ */ jsxs4("div", { className: cls.filterPill, children: [
    /* @__PURE__ */ jsx6(BiSearch, { size: 16, className: cls.filterIcon }),
    /* @__PURE__ */ jsx6(
      InputBase,
      {
        value: query,
        onChange: (event) => setQuery(event.target.value),
        placeholder: "Find page",
        sx: { color: "text.contrastPrimary", fontSize: 14, flex: 1 },
        inputProps: { "aria-label": "Find page" }
      }
    )
  ] });
  return /* @__PURE__ */ jsxs4("nav", { "aria-label": "Primary", className: cls.sidebar, children: [
    /* @__PURE__ */ jsxs4("div", { className: cls.scroll, children: [
      /* @__PURE__ */ jsxs4("div", { className: cls.header, children: [
        onBrandClick ? /* @__PURE__ */ jsx6("button", { type: "button", className: interactiveReset, onClick: onBrandClick, "aria-label": "Home", children: /* @__PURE__ */ jsx6("div", { className: cls.brand, children: condensed ? condensedLogo ?? logo : logo }) }) : /* @__PURE__ */ jsx6("div", { className: cls.brandStatic, children: condensed ? condensedLogo ?? logo : logo }),
        filterable && !pending ? condensed ? /* @__PURE__ */ jsxs4(Fragment2, { children: [
          /* @__PURE__ */ jsx6(Tooltip2, { title: "Find page", placement: "right", children: /* @__PURE__ */ jsx6(
            IconButton,
            {
              size: "small",
              onClick: (event) => setFilterAnchor(event.currentTarget),
              sx: { color: "text.contrastSecondary", display: "flex", mx: "auto", mb: "4px" },
              "aria-label": "Find page",
              children: /* @__PURE__ */ jsx6(BiSearch, { size: 18 })
            }
          ) }),
          /* @__PURE__ */ jsx6(
            Popover,
            {
              open: Boolean(filterAnchor),
              anchorEl: filterAnchor,
              onClose: () => setFilterAnchor(null),
              anchorOrigin: { vertical: "top", horizontal: "right" },
              transformOrigin: { vertical: "top", horizontal: "left" },
              slotProps: { paper: { sx: { ml: 1, p: 0, width: 220, bgcolor: "nav.sidebar" } } },
              children: filterField
            }
          )
        ] }) : /* @__PURE__ */ jsx6(Fragment2, { children: /* @__PURE__ */ jsx6("div", { className: cls.filterWrap, children: filterField }) }) : null
      ] }),
      /* @__PURE__ */ jsx6("div", { className: cls.list, children: visibleSections.filter((section) => section.entries.length > 0).map((section, index) => /* @__PURE__ */ jsx6(
        AppShellSidebarSection_default,
        {
          condensed,
          title: section.title,
          withDivider: index > 0,
          children: section.entries.map(renderEntry)
        },
        section.title ?? `section-${index}`
      )) }),
      footer ? /* @__PURE__ */ jsx6("div", { className: cls.footerBar, children: /* @__PURE__ */ jsx6("div", { className: cls.footer, children: React3.isValidElement(footer) ? React3.cloneElement(footer, { condensed }) : footer }) }) : null,
      /* @__PURE__ */ jsx6("div", { className: cx3(cls.loadingCover, !pending && cls.loadingCoverHidden), "aria-hidden": true, children: pending ? /* @__PURE__ */ jsx6(CircularProgress, { color: "primary" }) : null })
    ] }),
    resizable && !condensed ? /* @__PURE__ */ jsx6(
      "div",
      {
        className: cls.resizeHandle,
        onPointerDown: handleResizeStart,
        role: "separator",
        "aria-orientation": "vertical",
        "aria-label": "Resize sidebar"
      }
    ) : null
  ] });
};
var useStyles5 = (theme, props) => ({
  sidebar: css6`
    position: relative;
    /* Above the sticky topbar, so its shadow and blur don't bleed onto the sidebar's edge. */
    z-index: ${theme.zIndex.appBar + 1};
    display: flex;
    flex-direction: column;
    min-width: ${props.width}px;
    max-width: ${props.width}px;
    height: 100%;
    /* The inner .scroll owns the scrolling; keeping the nav non-scrolling pins the
       resize handle to the visible edge. */
    overflow: hidden;
    background-color: ${theme.palette.nav.sidebar};
    /* Right-edge divider, keeping the sidebar distinct from the page and topbar. */
    border-right: 1px solid ${theme.palette.divider};
    color: ${theme.palette.text.contrastPrimary};
    transition:
      min-width 0.2s ease-in-out,
      max-width 0.2s ease-in-out;
  `,
  /* The single scroll container: brand, filter, nav, and footer overflow together.
     position:relative anchors the loading cover to the visible body. */
  scroll: css6`
    position: relative;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
  `,
  /* The brand stays pinned to the top of the scroll as the nav scrolls under it.
     The ::after is a fade from the nav background to transparent just below the
     header, so nav items dissolve cleanly as they slide under it. */
  header: css6`
    position: sticky;
    top: 0;
    /* Above the loading cover (z 1) so the brand stays visible while the body loads. */
    z-index: 2;
    background-color: ${theme.palette.nav.sidebar};
    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 0;
      width: 100%;
      height: 12px;
      background: linear-gradient(to bottom, ${theme.palette.nav.sidebar}, transparent);
      /* Captures clicks so items scrolling under the fade can't be interacted with. */
      pointer-events: auto;
      user-select: none;
    }
  `,
  brand: css6`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 64px;
    padding: 0 6px;
  `,
  brandStatic: css6`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 64px;
    padding: 0 6px;
    cursor: default;
  `,
  filterWrap: css6`
    /* Matches the nav rows' 6px inset; the field carries no padding below it —
       the divider that follows owns the separation. */
    margin: 0px 12px;
  `,
  filterPill: css6`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 10px;
    border-radius: 6px;
    background-color: ${theme.palette.action.contrastHover};
  `,
  filterIcon: css6`
    flex-shrink: 0;
    color: ${theme.palette.text.contrastSecondary};
  `,
  /* Full-bleed divider: sits flush with the sidebar edges, no horizontal inset. */
  divider: css6`
    height: 1px;
    margin: 12px 0;
    background-color: ${theme.palette.dividerContrast};
  `,
  list: css6`
    /* Sizes to its content — the sidebar (not the list) owns the scroll now. It must
       NOT flex-grow: growing it would leave the footer's natural position mid-content,
       and the overflowing nav would then scroll straight past the sticky footer. */
    padding-top: 12px;
    padding-bottom: 24px;
  `,
  /* Pins the footer (account badge) to the bottom of the scroll. margin-top:auto
     drops it to the bottom when the nav is short; sticky keeps it there while the nav
     scrolls when it's tall. The ::before is a fade from transparent up to the nav
     background just above the footer, mirroring the header's fade. */
  footerBar: css6`
    position: sticky;
    bottom: 0;
    /* Above the loading cover (z 1) so the account badge stays visible while the body loads. */
    z-index: 2;
    margin-top: auto;
    background-color: ${theme.palette.nav.sidebar};
    &::before {
      content: '';
      position: absolute;
      bottom: 100%;
      left: 0;
      width: 100%;
      height: 24px;
      background: linear-gradient(to top, ${theme.palette.nav.sidebar}, transparent);
      /* Captures clicks so items scrolling under the fade can't be interacted with. */
      pointer-events: auto;
      user-select: none;
    }
  `,
  footer: css6`
    padding: 0px 12px 8px 12px;
  `,
  /* Solid nav-coloured cover over the body while the nav model resolves. Sits above the
     nav items (z 1) but below the sticky brand/footer (z 2); fades out on load. Anchored
     to the scroll's visible area, so during load (no overflow) it fills the body. */
  loadingCover: css6`
    position: absolute;
    inset: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${theme.palette.nav.sidebar};
    opacity: 1;
    transition: opacity 0.4s ease;
    pointer-events: none;
  `,
  loadingCoverHidden: css6`
    opacity: 0;
  `,
  resizeHandle: css6`
    position: absolute;
    top: 0;
    right: -3px;
    width: 6px;
    height: 100%;
    cursor: col-resize;
    touch-action: none;
    :hover,
    :active {
      background-color: ${theme.palette.action.contrastSelected};
    }
  `
});
var AppShellSidebar_default = AppShellSidebar;

// src/AppShellSidebar/AppShellSidebarAccountBadge.tsx
import React4 from "react";
import { css as css7, cx as cx4 } from "@emotion/css";
import { Avatar, Divider, IconButton as IconButton2, Menu as Menu2, MenuItem, Tooltip as Tooltip3, Typography as Typography4, useTheme as useTheme6 } from "@mui/material";
import { alpha as alpha2 } from "@mui/material/styles";
import { BiDotsVerticalRounded, BiLogOut, BiMessageRoundedDetail, BiSolidUser, BiUser } from "react-icons/bi";
import "@hopdrive/sdk-theme";
import { Fragment as Fragment3, jsx as jsx7, jsxs as jsxs5 } from "react/jsx-runtime";
var AppShellSidebarAccountBadge = ({ condensed, user, roleBadge, environment, onProfile, onSupport, onLogout }) => {
  const theme = useTheme6();
  const cls = useStyles6(theme);
  const [menuAnchor, setMenuAnchor] = React4.useState(null);
  const closeMenu = () => setMenuAnchor(null);
  const name = user?.name || "Unknown User";
  const email = user?.email || "Unknown Email";
  const showRoleBadge = Boolean(roleBadge) && environment !== "production";
  const menuItem = (handler, icon, label, danger) => handler ? /* @__PURE__ */ jsxs5(
    MenuItem,
    {
      onClick: () => {
        closeMenu();
        handler();
      },
      sx: danger ? { color: "error.main" } : void 0,
      children: [
        /* @__PURE__ */ jsx7("span", { className: cls.menuIcon, children: icon }),
        label
      ]
    }
  ) : null;
  const avatar = /* @__PURE__ */ jsxs5("span", { className: cx4(cls.avatarWrap, condensed && cls.avatarInteractive), children: [
    /* @__PURE__ */ jsx7(Avatar, { alt: name, src: user?.avatarUrl || void 0, sx: { width: 40, height: 40 }, children: /* @__PURE__ */ jsx7(BiSolidUser, { size: 20 }) }),
    /* @__PURE__ */ jsx7("span", { className: cls.avatarShadow })
  ] });
  return /* @__PURE__ */ jsxs5(Fragment3, { children: [
    condensed ? /* @__PURE__ */ jsx7(Tooltip3, { title: name, placement: "right", children: /* @__PURE__ */ jsx7(
      "button",
      {
        type: "button",
        className: cls.badgeCondensed,
        onClick: (event) => setMenuAnchor(event.currentTarget),
        "aria-label": "Account menu",
        children: avatar
      }
    ) }) : /* @__PURE__ */ jsxs5("div", { className: cls.badge, children: [
      avatar,
      /* @__PURE__ */ jsxs5("div", { className: cls.badgeInfo, children: [
        /* @__PURE__ */ jsx7(
          Typography4,
          {
            sx: {
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              lineHeight: 1.3,
              fontSize: 13,
              fontWeight: 600,
              color: "text.contrastPrimary"
            },
            children: name
          }
        ),
        /* @__PURE__ */ jsx7(
          Typography4,
          {
            sx: {
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              lineHeight: 1.3,
              fontSize: 11,
              fontWeight: 300,
              color: "text.contrastDisabled"
            },
            children: email
          }
        )
      ] }),
      /* @__PURE__ */ jsx7(
        IconButton2,
        {
          size: "small",
          onClick: (event) => setMenuAnchor(event.currentTarget),
          sx: { color: "text.contrastPrimary" },
          "aria-label": "Account menu",
          children: /* @__PURE__ */ jsx7(BiDotsVerticalRounded, { size: 20 })
        }
      )
    ] }),
    /* @__PURE__ */ jsxs5(Menu2, { anchorEl: menuAnchor, open: Boolean(menuAnchor), onClose: closeMenu, children: [
      /* @__PURE__ */ jsxs5("div", { className: cls.menuIdentity, children: [
        /* @__PURE__ */ jsx7(Typography4, { sx: { lineHeight: 1.25, fontWeight: 600 }, children: name }),
        /* @__PURE__ */ jsx7(Typography4, { variant: "body2", sx: { lineHeight: 1.25, color: "text.secondary" }, children: email }),
        showRoleBadge ? /* @__PURE__ */ jsx7("div", { className: cls.menuRole, children: roleBadge }) : null
      ] }),
      /* @__PURE__ */ jsx7(Divider, { sx: { my: "6px" } }),
      menuItem(onProfile, /* @__PURE__ */ jsx7(BiUser, { size: 16 }), "Profile"),
      menuItem(onSupport, /* @__PURE__ */ jsx7(BiMessageRoundedDetail, { size: 16 }), "Support"),
      onLogout ? /* @__PURE__ */ jsx7(Divider, { sx: { my: "6px" } }) : null,
      menuItem(onLogout, /* @__PURE__ */ jsx7(BiLogOut, { size: 16 }), "Logout", true)
    ] })
  ] });
};
var useStyles6 = (theme) => ({
  badge: css7`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 0;
    border-radius: 40px;
    background-color: ${alpha2(theme.palette.common.black, 0.25)};
  `,
  // A real <button>, so it resets native chrome itself.
  badgeCondensed: css7`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0;
    border: none;
    margin: 0;
    border-radius: 40px;
    background-color: transparent;
    cursor: pointer;
  `,
  badgeInfo: css7`
    flex: 1;
    min-width: 0;
  `,
  avatarWrap: css7`
    position: relative;
    display: inline-flex;
    flex-shrink: 0;
  `,
  avatarInteractive: css7`
    cursor: pointer;
    transition: transform 0.1s ease-in-out, opacity 0.1s ease-in-out;
    :hover,
    :focus,
    :active {
      opacity: 0.875;
      transform: scale(1.1);
    }
  `,
  avatarShadow: css7`
    position: absolute;
    inset: 0;
    border-radius: 50%;
    box-shadow: 0 0 6px 2px ${alpha2(theme.palette.common.black, 0.19)} inset;
    pointer-events: none;
  `,
  menuIdentity: css7`
    padding: 6px 16px;
  `,
  menuIcon: css7`
    display: inline-flex;
    margin-right: 8px;
  `,
  menuRole: css7`
    margin-top: 8px;
  `
});
var AppShellSidebarAccountBadge_default = AppShellSidebarAccountBadge;

// src/AppShellSidebar/BrandLogo.tsx
import { css as css8 } from "@emotion/css";

// src/assets/logo-banner-light.svg
var logo_banner_light_default = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 854.83 139.24"><defs><style>.cls-1{fill:%23fff;}.cls-2{fill:%23f44232;}</style></defs><g id="Layer_2" data-name="Layer 2"><g id="Layer_1-2" data-name="Layer 1"><path class="cls-1" d="M0,2.34H19V39.62C23.09,35.82,29,31.7,37.28,31.7c20,0,31.34,13.53,31.34,31.67v42.55H49.48V66c0-11.55-5.6-18.15-14.84-18.15-9.07,0-15.51,7.59-15.51,19.14v38.92H0Z"/><path class="cls-1" d="M79,69.8a37.94,37.94,0,1,1,37.78,38.1A37.88,37.88,0,0,1,79,69.8Zm38.11,20a19.3,19.3,0,1,0-19.3-19.3A19.33,19.33,0,0,0,117.15,89.76Z"/><path class="cls-1" d="M163.53,69.8a38,38,0,1,1,76,0c0,21-16,38.1-36.62,38.1A31.41,31.41,0,0,1,182.66,100v39.25h-19Zm38.1,20a19.3,19.3,0,1,0-19.3-19.3A19.32,19.32,0,0,0,201.63,89.76Z"/><path class="cls-1" d="M305.12,2.34h19V69.8a38,38,0,1,1-76,0c0-21.11,16-38.1,36.62-38.1a32.3,32.3,0,0,1,20.45,7.75ZM286,89.76a19.3,19.3,0,1,0-19.3-19.3A19.22,19.22,0,0,0,286,89.76Z"/><path class="cls-1" d="M335.85,65.18c0-17.32,13.19-31.83,34.63-31.83v17c-9.23,0-15.83,5.61-15.83,17.15v38.43h-18.8Z"/><path class="cls-1" d="M382.1,33.35h19v72.57h-19Zm.33-16.17a9.49,9.49,0,1,1,19,0,9.49,9.49,0,0,1-19,0Z"/><path class="cls-1" d="M408.33,33.35H430.6l18,38.76L468,33.35h20.62l-40.08,77Z"/><path class="cls-1" d="M555.34,97.68a43.7,43.7,0,0,1-27.88,9.73c-22.76,0-41.07-16.5-41.07-37.77s16.66-37.78,39.42-37.78,37.61,16.5,37.61,37.78v5.44H505.36c2.64,9.57,11.88,16.33,22.1,16.33a21.9,21.9,0,0,0,16.66-7.09ZM543.46,59.57a18.82,18.82,0,0,0-17.65-11.71A20.56,20.56,0,0,0,507,59.57Z"/><rect class="cls-2" x="577.66" y="33.38" width="28.43" height="10.78"/><rect class="cls-2" x="604.64" y="53.96" width="29.5" height="10.78"/><polygon class="cls-2" points="673.65 95.15 673.65 105.83 694.89 105.79 687.78 95.15 673.65 95.15"/><polygon class="cls-2" points="620.36 105.93 660.79 105.85 660.79 95.15 620.36 95.14 620.36 105.93"/><rect class="cls-2" x="593.27" y="95.12" width="12.85" height="10.78"/><path class="cls-2" d="M801.86,70.77c.87-1.26.88-1.26,2.33-.85a58.51,58.51,0,0,0,14,1.91,64.29,64.29,0,0,0,12.33-.31,35.87,35.87,0,0,0,20.69-10.6,11.53,11.53,0,0,0,3.4-10.69,10.45,10.45,0,0,0-7.05-8.43q-14-4.95-28.08-9.85l-16.63-5.84a7.41,7.41,0,0,1-1.77-.77A215.15,215.15,0,0,0,780.39,12a87.14,87.14,0,0,0-21.19-8.6,107.41,107.41,0,0,0-22-2.63C733.09.58,728.92.5,724.76.36c-3.2-.11-6.39-.34-9.58-.36a7.47,7.47,0,0,0-7.54,8.47,30.27,30.27,0,0,0,1.16,4.94c2.35,7.8,7.12,13.49,14.77,16.59a46.77,46.77,0,0,0,14.72,3.07c5.54.35,11.08.48,16.62.73,3.61.16,7.22.2,10.81.55,4.84.48,9.65,1.19,14.48,1.83,1.3.17,2.59.43,3.91.65,0,.11-.05.17-.08.19-.72.39-1.44.8-2.17,1.17a56.52,56.52,0,0,1-28.57,6.14c-6.69-.27-13.12-1.85-19.61-3.46-14.5-3.59-21.09-4.48-34.6-6.5-2.65-.4-8.61-.64-14.1-.78l.14,10.58H664.2V33.53L616,33.37V44.16h41.37L663,54,646.5,54V64.74l21.67.2c2.09,3.31,4.17,6.55,6.17,9.6h-20v10.7l27,0c3,4.53,5.31,8.06,6.37,10l19.95.21.55,10.42,7.53-.15c9.63-.36,15.4-1.8,25.42-3.6a11.49,11.49,0,0,0,2.55-1.17l.11.13c-.23.39-.45.79-.68,1.19a23.62,23.62,0,0,0-2.59,5.89c-.83,3.47-.18,6.51,2.68,8.84a10,10,0,0,0,6.11,2,35.19,35.19,0,0,0,10.55-1.4c13-3.45,25.86-7.19,38.46-11.88a81.72,81.72,0,0,1,13.79-4.24,12.72,12.72,0,0,1,9.6,1.7,44.12,44.12,0,0,0,6.08,3.21,23.63,23.63,0,0,0,8.57,2.34c3.56.13,6.88-.56,9.56-3.06s3.93-5.58,3-9.26a8.33,8.33,0,0,0-4.8-5.44,57.53,57.53,0,0,0-7-2.72,103.62,103.62,0,0,1-12.62-4.74A33,33,0,0,0,810,80.3a39,39,0,0,0-11.34,1.92l-2.46.78c.08-.34.12-.55.18-.75A41.46,41.46,0,0,1,801.86,70.77Zm-128.21-6V54L707.11,54V64.74Zm52.76,20.4H689.2V74.55h37.21Z"/><polygon class="cls-2" points="641.47 74.55 614.68 74.54 614.68 85.33 641.47 85.28 641.47 74.55"/><rect class="cls-2" x="580.42" y="53.96" width="12.85" height="10.78"/></g></g></svg>';

// src/assets/rabbit.svg
var rabbit_default = 'data:image/svg+xml,<?xml version="1.0" encoding="utf-8"?>%0A<!-- Generator: Adobe Illustrator 27.5.0, SVG Export Plug-In . SVG Version: 6.00 Build 0)  -->%0A<svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"%0A%09 viewBox="0 0 2000 2000" style="enable-background:new 0 0 2000 2000;" xml:space="preserve">%0A<style type="text/css">%0A%09.st0{fill:%23EF4434;}%0A</style>%0A<g>%0A%09<path class="st0" d="M463.93,1357.39c101.1,31.29,399.47,7.71,406.89,4.3c0.32,0.38,0.64,0.76,0.96,1.14%0A%09%09c-1.97,3.48-3.95,6.95-5.9,10.43c-9.21,16.46-18.24,33.03-22.68,51.55c-7.26,30.27-1.54,56.91,23.46,77.26%0A%09%09c15.42,12.55,34.05,16.66,53.43,17.44c31.42,1.26,62.08-4.25,92.21-12.26c113.49-30.15,226.19-62.89,336.33-103.94%0A%09%09c39.43-14.69,78.91-29.46,120.56-37.02c29.79-5.4,57.73-2.11,84.01,14.81c16.81,10.82,34.82,20.13,53.16,28.14%0A%09%09c23.82,10.41,48.33,19.39,74.9,20.4c31.14,1.17,60.19-4.9,83.57-26.77c23.39-21.88,34.37-48.75,25.91-80.9%0A%09%09c-5.94-22.55-21.63-38-41.99-47.61c-19.63-9.26-40.06-17.14-60.72-23.81c-37.47-12.09-74.65-24.49-110.39-41.38%0A%09%09c-40.14-18.97-82.95-27.72-127.48-27.26c-33.93,0.35-66.88,6.68-99.16,16.82c-6.93,2.18-13.85,4.37-21.51,6.78%0A%09%09c0.69-2.9,1.02-4.76,1.57-6.54c11.03-35.85,26.76-69.46,48.11-100.36c7.61-11.02,7.67-11,20.33-7.43%0A%09%09c40.01,11.29,81.04,15.26,122.39,16.73c35.98,1.28,71.99,2.88,107.8-2.75c70.46-11.09,131.05-41.4,180.94-92.68%0A%09%09c25.54-26.25,36.66-57.43,29.67-93.5c-6.87-35.45-26.67-61.4-61.66-73.74c-81.78-28.83-163.65-57.42-245.48-86.11%0A%09%09c-48.5-17.01-97-33.99-145.47-51.09c-5.3-1.87-10.98-3.54-15.46-6.74c-58.46-41.75-117.87-81.99-180.79-116.83%0A%09%09c-58.75-32.53-119.94-59.02-185.31-75.27c-63.04-15.67-127.38-20.45-191.98-22.93c-36.4-1.4-72.82-2.11-109.22-3.34%0A%09%09c-27.93-0.94-55.84-2.95-83.77-3.17c-40.43-0.3-71.09,34.15-65.93,74.08c1.89,14.59,5.86,29.04,10.14,43.18%0A%09%09c20.62,68.21,62.31,117.98,129.16,145.06c41.26,16.72,84.7,24.04,128.77,26.82c48.38,3.06,96.87,4.27,145.31,6.41%0A%09%09c31.52,1.39,63.16,1.74,94.52,4.82c42.31,4.16,84.42,10.4,126.58,15.96c11.41,1.51,22.72,3.78,34.22,5.72%0A%09%09c-0.38,0.94-0.41,1.5-0.68,1.65c-6.31,3.46-12.57,7-18.98,10.27c-78.5,40.15-161.96,57.21-249.85,53.67%0A%09%09c-58.51-2.36-114.73-16.2-171.45-30.25c-126.79-31.4-476.19-36.29-629.59-17.28"/>%0A</g>%0A</svg>%0A';

// src/AppShellSidebar/BrandLogo.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
var BrandLogo = ({ logoUrl, name, condensed }) => {
  const cls = useStyles7();
  const branded = Boolean(logoUrl);
  if (branded) {
    return /* @__PURE__ */ jsx8(
      "img",
      {
        className: condensed ? cls.brandedRail : cls.brandedExpanded,
        src: logoUrl,
        alt: name ?? "Logo"
      }
    );
  }
  return condensed ? /* @__PURE__ */ jsx8("img", { className: cls.rabbit, src: rabbit_default, alt: "HopDrive" }) : /* @__PURE__ */ jsx8("img", { className: cls.banner, src: logo_banner_light_default, alt: "HopDrive" });
};
var logoImg = `
  display: block;
  width: auto;
  height: auto;
  max-width: 100%;
  object-fit: contain;
`;
var useStyles7 = () => ({
  // The HopDrive banner keeps a fixed size regardless of sidebar width (matching
  // the legacy shell): it does not scale up as the sidebar widens, and overflows
  // rather than shrinking when the sidebar is narrow.
  banner: css8`
    display: block;
    width: 180px;
    max-height: 30px;
    object-fit: contain;
  `,
  rabbit: css8`
    ${logoImg}
    max-height: 40px;
  `,
  brandedExpanded: css8`
    ${logoImg}
    max-height: 36px;
  `,
  brandedRail: css8`
    ${logoImg}
    max-height: 40px;
  `
});
var BrandLogo_default = BrandLogo;

// src/AppShellTopbar/AppShellTopbar.tsx
import { css as css9 } from "@emotion/css";
import { Typography as Typography5, useTheme as useTheme7 } from "@mui/material";
import { BiMenuAltLeft } from "react-icons/bi";
import "@hopdrive/sdk-theme";

// src/AppShellTopbar/AppShellTopbarIconButton.tsx
import { IconButton as IconButton3, Tooltip as Tooltip4 } from "@mui/material";
import { jsx as jsx9 } from "react/jsx-runtime";
var AppShellTopbarIconButton = ({ icon: Icon, label, onClick, size = 20, disabled }) => /* @__PURE__ */ jsx9(Tooltip4, { title: label, children: /* @__PURE__ */ jsx9("span", { children: /* @__PURE__ */ jsx9(IconButton3, { onClick, disabled, "aria-label": label, sx: { color: "text.primary" }, children: /* @__PURE__ */ jsx9(Icon, { size }) }) }) });
var AppShellTopbarIconButton_default = AppShellTopbarIconButton;

// src/AppShellTopbar/AppShellTopbar.tsx
import { jsx as jsx10, jsxs as jsxs6 } from "react/jsx-runtime";
var AppShellTopbar = ({ pageName, appName = "HopDrive", onToggleNav, navCondensed, children }) => {
  const theme = useTheme7();
  const cls = useStyles8(theme);
  const title = pageName || appName;
  return /* @__PURE__ */ jsxs6("header", { className: cls.bar, children: [
    onToggleNav ? /* @__PURE__ */ jsx10(
      AppShellTopbarIconButton_default,
      {
        icon: BiMenuAltLeft,
        size: 24,
        label: navCondensed ? "Expand menu" : "Collapse menu",
        onClick: onToggleNav
      }
    ) : null,
    /* @__PURE__ */ jsx10(
      Typography5,
      {
        component: "h1",
        sx: { overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", minWidth: 0, fontSize: 18, fontWeight: 500 },
        children: title
      }
    ),
    /* @__PURE__ */ jsx10("div", { className: cls.slots, children })
  ] });
};
var useStyles8 = (theme) => ({
  bar: css9`
    z-index: ${theme.zIndex.appBar};
    position: sticky;
    top: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-height: 64px;
    max-height: 64px;
    overflow: hidden;
    padding: 0 16px;
    border-bottom: 1px solid ${theme.palette.divider};
    background-color: ${theme.palette.nav.header};
    box-shadow: ${theme.palette.mode === "light" ? `0 0 16px ${theme.palette.divider}` : "none"};
    backdrop-filter: blur(6px);
    color: ${theme.palette.text.primary};
  `,
  slots: css9`
    display: flex;
    align-items: center;
    gap: 4px;
    margin-left: auto;
    max-height: 64px;
    overflow: hidden;
  `
});
var AppShellTopbar_default = AppShellTopbar;

// src/AppShell/AppShell.tsx
import React6 from "react";
import { css as css12 } from "@emotion/css";
import { useTheme as useTheme11 } from "@mui/material";
import { alpha as alpha3 } from "@mui/material/styles";

// src/AppShellPage/AppShellPage.tsx
import { css as css11, cx as cx5 } from "@emotion/css";
import { useTheme as useTheme8 } from "@mui/material";

// src/AppShellPage/AppShellPageBanner.tsx
import { css as css10 } from "@emotion/css";
import { jsx as jsx11 } from "react/jsx-runtime";
var AppShellPageBanner = ({ children }) => {
  const cls = useStyles9();
  if (!children) return null;
  return /* @__PURE__ */ jsx11("div", { className: cls.region, children: /* @__PURE__ */ jsx11("div", { className: cls.content, children }) });
};
var useStyles9 = () => ({
  region: css10`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 2;
    display: flex;
    justify-content: center;
    padding: 12px;
    pointer-events: none;
  `,
  content: css10`
    pointer-events: auto;
    max-width: 100%;
  `
});
var AppShellPageBanner_default = AppShellPageBanner;

// src/AppShellPage/AppShellPage.tsx
import { jsx as jsx12, jsxs as jsxs7 } from "react/jsx-runtime";
var AppShellPage = ({ scroll = "page", header, banner, id, children }) => {
  const theme = useTheme8();
  const cls = useStyles10(theme);
  return /* @__PURE__ */ jsxs7("main", { id, tabIndex: id ? -1 : void 0, className: cls.page, children: [
    /* @__PURE__ */ jsxs7("div", { className: cx5(cls.viewport, scroll === "page" ? cls.viewportScroll : cls.viewportFixed), children: [
      header,
      scroll === "page" ? children : /* @__PURE__ */ jsx12("div", { className: cls.paneHost, children })
    ] }),
    /* @__PURE__ */ jsx12(AppShellPageBanner_default, { children: banner })
  ] });
};
var useStyles10 = (theme) => ({
  // position:relative makes the page the banner's containing block; overflow:hidden
  // keeps the banner pinned to the viewport while the inner viewport scrolls.
  page: css11`
    position: relative;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background-color: ${theme.palette.background.default};
  `,
  // The scroll container the sticky header and the page content share.
  viewport: css11`
    flex: 1;
    min-height: 0;
  `,
  // 'page': a plain block scroll container. The sticky header still pins, and block
  // children fill the width — so a page's own `mx: auto` / max-width centers against
  // the full width. (A flex column here would stop such children from stretching,
  // shrinking them to content width with invisible auto-margin space on the sides.)
  viewportScroll: css11`
    overflow-y: auto;
  `,
  // 'none': flex column so the pane host can flex-grow to fill the height.
  viewportFixed: css11`
    overflow: hidden;
    display: flex;
    flex-direction: column;
  `,
  // Full-bleed surfaces ('none') manage their own panes, so give them a flex host.
  paneHost: css11`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  `
});
var AppShellPage_default = AppShellPage;

// src/AppShell/AppShellToasts.tsx
import { useTheme as useTheme9 } from "@mui/material";
import { ToastContainer, Zoom } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "@hopdrive/sdk-theme";
import { jsx as jsx13 } from "react/jsx-runtime";
var AppShellToasts = ({ limit = 3 }) => {
  const theme = useTheme9();
  const themedVars = {
    zIndex: theme.zIndex.snackbar,
    // Dark nav surface for the toast background and ink; semantics and progress
    // from the theme palette.
    "--toastify-color-dark": theme.palette.nav.sidebar,
    "--toastify-text-color-dark": theme.palette.nav.contrastText,
    "--toastify-color-light": theme.palette.background.paper,
    "--toastify-text-color-light": theme.palette.text.primary,
    "--toastify-color-info": theme.palette.info.main,
    "--toastify-color-success": theme.palette.success.main,
    "--toastify-color-warning": theme.palette.warning.main,
    "--toastify-color-error": theme.palette.error.main,
    "--toastify-color-progress-light": theme.palette.primary.main,
    "--toastify-color-progress-dark": theme.palette.primary.main,
    "--toastify-font-family": theme.typography.fontFamily
  };
  return /* @__PURE__ */ jsx13(
    ToastContainer,
    {
      position: "top-center",
      theme: "dark",
      limit,
      transition: Zoom,
      style: themedVars
    }
  );
};
var AppShellToasts_default = AppShellToasts;

// src/AppShell/useShellResponsiveState.ts
import React5 from "react";
import { useMediaQuery, useTheme as useTheme10 } from "@mui/material";

// src/utils/resolveNavPresentation.ts
function resolveNavPresentation(viewport, collapsedPref, drawerOpen) {
  if (viewport === "overlay") {
    return drawerOpen ? { mode: "drawer", scrim: true } : { mode: "hidden", scrim: false };
  }
  return collapsedPref ? { mode: "rail", scrim: false } : { mode: "expanded", scrim: false };
}

// src/AppShell/useShellResponsiveState.ts
var readStored = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
var writeStored = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
  }
};
function useShellResponsiveState({
  breakpoint = "lg",
  storageKey = "hopdrive-shell",
  defaultNavCondensed = false
} = {}) {
  const theme = useTheme10();
  const isOverlay = useMediaQuery(theme.breakpoints.down(breakpoint));
  const viewport = isOverlay ? "overlay" : "desktop";
  const collapsedKey = `${storageKey}.navCondensed`;
  const widthKey = `${storageKey}.navWidth`;
  const [collapsedPref, setCollapsedPref] = React5.useState(() => {
    const stored = readStored(collapsedKey);
    return stored === null ? defaultNavCondensed : stored === "true";
  });
  const [width, setWidthState] = React5.useState(() => {
    const stored = readStored(widthKey);
    return clampSidebarWidth(stored === null ? void 0 : Number(stored));
  });
  const [drawerOpen, setDrawerOpen] = React5.useState(false);
  React5.useEffect(() => {
    if (!isOverlay) setDrawerOpen(false);
  }, [isOverlay]);
  const toggle = () => {
    if (viewport === "overlay") {
      setDrawerOpen((open) => !open);
      return;
    }
    setCollapsedPref((prev) => {
      writeStored(collapsedKey, String(!prev));
      return !prev;
    });
  };
  const closeDrawer = () => setDrawerOpen(false);
  const setWidth = (candidate) => {
    const clamped = clampSidebarWidth(candidate);
    writeStored(widthKey, String(clamped));
    setWidthState(clamped);
  };
  const presentation = resolveNavPresentation(viewport, collapsedPref, drawerOpen);
  return {
    viewport,
    presentation,
    width,
    navCondensed: presentation.mode === "rail" || presentation.mode === "hidden",
    toggle,
    closeDrawer,
    setWidth
  };
}

// src/AppShell/AppShell.tsx
import { jsx as jsx14, jsxs as jsxs8 } from "react/jsx-runtime";
var CONTENT_ID = "app-shell-content";
var AppShell = ({
  sections,
  currentPath,
  navigate,
  navPending,
  navFilterable = true,
  logo,
  condensedLogo,
  onBrandClick,
  sidebarFooter,
  pageName,
  appName,
  barContent,
  scroll,
  banner,
  children,
  chromeless,
  breakpoint,
  storageKey,
  defaultNavCondensed,
  toastLimit
}) => {
  const theme = useTheme11();
  const cls = useStyles11(theme);
  const shell = useShellResponsiveState({ breakpoint, storageKey, defaultNavCondensed });
  const { presentation } = shell;
  const drawerRef = React6.useRef(null);
  const openerRef = React6.useRef(null);
  React6.useEffect(() => {
    if (presentation.mode === "drawer") {
      openerRef.current = document.activeElement;
      drawerRef.current?.focus();
    } else if (openerRef.current instanceof HTMLElement) {
      openerRef.current.focus();
      openerRef.current = null;
    }
  }, [presentation.mode]);
  if (chromeless) {
    return /* @__PURE__ */ jsxs8("div", { className: cls.shell, children: [
      /* @__PURE__ */ jsx14(AppShellPage_default, { id: CONTENT_ID, scroll, banner, children }),
      /* @__PURE__ */ jsx14(AppShellToasts_default, { limit: toastLimit })
    ] });
  }
  const navigateAndSettle = navigate ? (route) => {
    shell.closeDrawer();
    navigate(route);
  } : void 0;
  const brandClickAndSettle = onBrandClick ? () => {
    shell.closeDrawer();
    onBrandClick();
  } : void 0;
  const sidebar = /* @__PURE__ */ jsx14(
    AppShellSidebar_default,
    {
      sections,
      currentPath,
      navigate: navigateAndSettle,
      pending: navPending,
      filterable: navFilterable,
      logo,
      condensedLogo,
      onBrandClick: brandClickAndSettle,
      footer: sidebarFooter,
      condensed: presentation.mode === "rail",
      width: shell.width,
      resizable: presentation.mode === "expanded",
      onWidthChange: shell.setWidth
    }
  );
  return /* @__PURE__ */ jsxs8("div", { className: cls.shell, children: [
    /* @__PURE__ */ jsx14("a", { href: `#${CONTENT_ID}`, className: cls.skipLink, children: "Skip to content" }),
    presentation.mode === "expanded" || presentation.mode === "rail" ? sidebar : null,
    presentation.mode === "drawer" ? /* @__PURE__ */ jsx14("div", { ref: drawerRef, tabIndex: -1, className: cls.drawer, children: sidebar }) : null,
    presentation.scrim ? /* @__PURE__ */ jsx14("div", { className: cls.scrim, onClick: shell.closeDrawer, "aria-hidden": true }) : null,
    /* @__PURE__ */ jsx14("div", { className: cls.content, children: /* @__PURE__ */ jsx14(
      AppShellPage_default,
      {
        id: CONTENT_ID,
        scroll,
        banner,
        header: /* @__PURE__ */ jsx14(AppShellTopbar_default, { pageName, appName, onToggleNav: shell.toggle, navCondensed: shell.navCondensed, children: barContent }),
        children
      }
    ) }),
    /* @__PURE__ */ jsx14(AppShellToasts_default, { limit: toastLimit })
  ] });
};
var useStyles11 = (theme) => ({
  // The app mounts the shell in a full-viewport element; the shell fills it.
  shell: css12`
    position: relative;
    display: flex;
    width: 100%;
    height: 100%;
    overflow: hidden;
  `,
  skipLink: css12`
    position: absolute;
    top: -100%;
    left: 8px;
    z-index: ${theme.zIndex.tooltip};
    padding: 8px 12px;
    border-radius: 6px;
    background-color: ${theme.palette.background.paper};
    color: ${theme.palette.text.primary};
    :focus {
      top: 8px;
    }
  `,
  drawer: css12`
    z-index: ${theme.zIndex.drawer};
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    outline: none;
  `,
  scrim: css12`
    z-index: ${theme.zIndex.drawer - 1};
    position: fixed;
    inset: 0;
    background-color: ${alpha3(theme.palette.common.black, 0.5)};
  `,
  content: css12`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  `
});
var AppShell_default = AppShell;
export {
  AppShell_default as AppShell,
  AppShellPage_default as AppShellPage,
  AppShellPageBanner_default as AppShellPageBanner,
  AppShellSidebar_default as AppShellSidebar,
  AppShellSidebarAccountBadge_default as AppShellSidebarAccountBadge,
  AppShellSidebarItem_default as AppShellSidebarItem,
  AppShellSidebarItemGroup_default as AppShellSidebarItemGroup,
  AppShellSidebarSection_default as AppShellSidebarSection,
  AppShellToasts_default as AppShellToasts,
  AppShellTopbar_default as AppShellTopbar,
  AppShellTopbarIconButton_default as AppShellTopbarIconButton,
  BrandLogo_default as BrandLogo,
  NavBadge_default as NavBadge,
  NavRow_default as NavRow,
  SIDEBAR_RAIL_WIDTH,
  SIDEBAR_WIDTH_DEFAULT,
  SIDEBAR_WIDTH_MAX,
  SIDEBAR_WIDTH_MIN,
  clampSidebarWidth,
  filterNavSections,
  matchRoute,
  resolveActiveEntry,
  resolveNavPresentation,
  useShellResponsiveState
};
//# sourceMappingURL=index.mjs.map